import {
  ButtonProps,
  Button as ChakraButton,
  StyleConfig,
  defineStyleConfig
} from '@chakra-ui/react';
import { darken } from 'polished';
import { LegacyRef, forwardRef } from 'react';

export { ButtonGroup, ButtonSpinner } from '@chakra-ui/react';
export type {
  ButtonGroupProps,
  ButtonOptions,
  ButtonProps
} from '@chakra-ui/react';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'alert'
  | 'unstyled'
  | 'ghost'
  | 'link'
  | 'outline';

export const Button = forwardRef(function Button(
  props: ButtonProps,
  ref
): JSX.Element {
  const { ...rest } = props;
  return (
    <ChakraButton
      borderRadius="radii.x2"
      ref={ref as LegacyRef<HTMLButtonElement>}
      {...rest}
    />
  );
});

export const ButtonTheme: StyleConfig = defineStyleConfig({
  baseStyle: {
    boxSizing: 'border-box',
    borderWidth: '1px',
    borderStyle: 'solid',
    transition: 'all 250ms ease',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    _disabled: {
      cursor: 'not-allowed',
      _hover: {
        backgroundColor: 'inherit',
        borderColor: 'inherit'
      }
    }
  },
  variants: {
    primary: ({ theme: { colors } }) => ({
      color: colors.palette_white,
      backgroundColor: colors.palette_primary[100],
      borderColor: colors.palette_primary[100],
      _hover: {
        backgroundColor: colors.palette_primary[125],
        borderColor: colors.palette_primary[125],
        textDecoration: 'none'
      },
      _disabled: {
        color: colors.palette_gray[80],
        backgroundColor: colors.palette_gray[10],
        borderColor: colors.palette_gray[10]
      },
      _loading: {
        color: colors.palette_white,
        backgroundColor: colors.palette_primary[100],
        borderColor: colors.palette_primary[100],
        _hover: {
          color: colors.palette_white,
          backgroundColor: colors.palette_primary[100],
          borderColor: colors.palette_primary[100],
          textDecoration: 'none'
        }
      }
    }),
    secondary: ({ theme: { colors } }) => ({
      color: colors.palette_primary[100],
      backgroundColor: 'transparent',
      borderColor: colors.palette_primary[100],
      _hover: {
        backgroundColor: colors.palette_primary[0],
        color: colors.palette_primary[100],
        borderColor: colors.palette_primary[100]
      },
      _disabled: {
        color: colors.palette_gray[60],
        backgroundColor: 'transparent',
        borderColor: colors.palette_gray[40],
        _hover: {
          color: colors.palette_gray[60]
        }
      },
      _loading: {
        color: colors.palette_primary[100],
        backgroundColor: 'transparent',
        borderColor: colors.palette_primary[100],
        _hover: {
          color: colors.palette_primary[100],
          backgroundColor: 'transparent',
          borderColor: colors.palette_primary[100]
        }
      }
    }),
    tertiary: ({ theme: { colors } }) => ({
      color: colors.palette_primary[100],
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      _hover: {
        color: colors.palette_primary[125]
      },
      _disabled: {
        opacity: 1,
        color: colors.palette_gray[60],
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        _hover: {
          color: colors.palette_gray[60],
          backgroundColor: 'transparent',
          borderColor: 'transparent'
        }
      },
      _loading: {
        color: colors.palette_primary[100],
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        _hover: {
          color: colors.palette_gray[60],
          backgroundColor: 'transparent',
          borderColor: 'transparent'
        }
      }
    }),
    success: ({ theme: { colors } }) => ({
      color: colors.palette_success[100],
      backgroundColor: colors.palette_success[5],
      borderColor: colors.palette_success[5],
      _hover: {
        backgroundColor: darken(0.05, colors.palette_success[5]),
        borderColor: darken(0.05, colors.palette_success[5])
      },
      _disabled: {
        color: colors.palette_gray[60],
        backgroundColor: colors.palette_gray[5],
        borderColor: colors.palette_gray[5]
      },
      _loading: {
        color: colors.palette_success[100],
        backgroundColor: colors.palette_success[5],
        borderColor: colors.palette_success[5],
        _hover: {
          color: colors.palette_success[100],
          backgroundColor: colors.palette_success[5],
          borderColor: colors.palette_success[5]
        }
      }
    }),
    alert: ({ theme: { colors } }) => ({
      color: colors.palette_alert[100],
      backgroundColor: colors.palette_alert[5],
      borderColor: colors.palette_alert[5],
      _hover: {
        backgroundColor: darken(0.05, colors.palette_alert[5]),
        borderColor: darken(0.05, colors.palette_alert[5])
      },
      _disabled: {
        color: colors.palette_gray[60],
        backgroundColor: colors.palette_gray[5],
        borderColor: colors.palette_gray[5]
      },
      _loading: {
        color: colors.palette_alert[100],
        backgroundColor: colors.palette_alert[5],
        borderColor: colors.palette_alert[5],
        _hover: {
          color: colors.palette_alert[100],
          backgroundColor: colors.palette_alert[5],
          borderColor: colors.palette_alert[5]
        }
      }
    })
  },
  sizes: {
    large: {
      textStyle: 'h300',
      height: '44px',
      padding: '12px 24px',
      borderRadius: '10px'
    },
    medium: {
      textStyle: 'h200',
      height: '36px',
      padding: '8px 16px',
      borderRadius: '8px'
    },
    small: {
      textStyle: 'h100',
      height: '28px',
      padding: '4px 12px',
      borderRadius: '6px'
    }
  },
  defaultProps: {
    variant: 'primary',
    size: 'medium'
  }
});
